"use client";

import { Button } from "@ethera/ui/button";
import { SOCIAL_LINKS } from "@guru/shared/constants/social-links";
import { useTranslation } from "@guru/shared/i18n";
import { Layers, type LucideIcon, Mail, MessageCircle, Puzzle, RefreshCw } from "lucide-react";
import { FaqList } from "@/components/marketing/faq-list";
import { ScrollReveal } from "@/components/public/scroll-reveal";

/**
 * The dedicated pricing FAQ is long, so it reads better clustered by topic.
 * Each group maps to indices in the ordered translation array — copy lives in
 * i18n, only the grouping and icons live here. Indices are guarded so a locale
 * with a shorter list never renders a hole.
 */
const FAQ_GROUPS: { label: string; icon: LucideIcon; indices: number[] }[] = [
  { label: "Messages & limits", icon: MessageCircle, indices: [0, 1, 5] },
  { label: "Plans & value", icon: Layers, indices: [2, 6, 8] },
  { label: "Add-ons", icon: Puzzle, indices: [3] },
  { label: "Billing & changes", icon: RefreshCw, indices: [4, 7] },
];

export function PricingFAQ() {
  const { t } = useTranslation();
  const allItems = t.pricing.dedicatedFaq.items;
  const whatsappLink = SOCIAL_LINKS.find((s) => s.name === "WhatsApp")?.href;

  return (
    <section className="relative overflow-clip bg-background py-16 sm:py-20">
      <div
        className="ledger-grid ledger-grid-fade pointer-events-none absolute inset-0"
        aria-hidden="true"
      />

      <div className="relative z-10 mx-auto w-full max-w-3xl px-4 sm:px-6 lg:px-8">
        <ScrollReveal className="mb-12 text-center">
          <p className="mb-3 text-sm font-semibold uppercase tracking-widest text-brand-deep">
            Before you decide
          </p>
          <h2 className="text-balance text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
            {t.pricing.dedicatedFaq.title}
          </h2>
        </ScrollReveal>

        <div className="space-y-10">
          {FAQ_GROUPS.map((group) => {
            const items = group.indices.map((i) => allItems[i]).filter(Boolean);
            if (items.length === 0) return null;
            const Icon = group.icon;

            return (
              <ScrollReveal key={group.label} threshold={0.1}>
                {/* Group header */}
                <div className="mb-3 flex items-center gap-3">
                  <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-brand/10 text-brand-deep ring-1 ring-brand/15">
                    <Icon className="size-[18px]" aria-hidden="true" />
                  </span>
                  <h3 className="text-lg font-semibold text-foreground">{group.label}</h3>
                  <span className="ml-auto rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
                    {items.length} {items.length === 1 ? "question" : "questions"}
                  </span>
                </div>

                <FaqList
                  items={items}
                  icons={items.map(() => Icon)}
                  eventName="pricing_faq_expand"
                  idPrefix={`pricing-${group.label.replace(/\s+/g, "-").toLowerCase()}`}
                />
              </ScrollReveal>
            );
          })}
        </div>

        {/* Escape hatch — most "should I pick X" questions end in a conversation */}
        <ScrollReveal threshold={0.2}>
          <div className="mt-12 overflow-hidden rounded-2xl border border-border bg-secondary/40 p-6 text-center sm:p-8">
            <h3 className="text-lg font-semibold text-foreground">Still weighing it up?</h3>
            <p className="mx-auto mt-2 max-w-md text-pretty text-sm leading-relaxed text-muted-foreground">
              Tell us your numbers and we'll point you to the plan that fits — no sales pitch.
            </p>
            <div className="mt-5 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Button
                asChild
                size="lg"
                className="h-11 gap-2 bg-brand font-semibold text-brand-contrast hover:bg-brand/90"
              >
                <a href="mailto:hello@etheraguru.com">
                  <Mail className="size-4" aria-hidden="true" />
                  Email hello@etheraguru.com
                </a>
              </Button>
              {whatsappLink && (
                <Button asChild variant="outline" size="lg" className="h-11 gap-2">
                  <a href={whatsappLink} target="_blank" rel="noopener noreferrer">
                    <MessageCircle className="size-4" aria-hidden="true" />
                    Chat on WhatsApp
                  </a>
                </Button>
              )}
            </div>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}
